import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { PackageX, Trash2 } from 'lucide-react';

import type { ClusterDetailResponseOutputPhotosItem } from '@/api/generated';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type ClusterMark = 'junk' | 'wrongSku';

export const MARK_STYLES: Record<ClusterMark, string> = {
  junk: 'bg-removed border-removed text-white hover:bg-removed/90',
  wrongSku:
    'bg-destructive border-destructive text-white hover:bg-destructive/90',
};

export const MarkIcon: FC<{ mark: ClusterMark }> = ({ mark }) =>
  mark === 'junk' ? <Trash2 /> : <PackageX />;

export const PhotoFrame: FC<{
  src: string | null;
  alt: string;
  className?: string;
}> = ({ src, alt, className }) => (
  <div
    className={cn(
      'bg-muted relative grid aspect-[3/4] place-items-center overflow-hidden rounded-lg',
      className
    )}>
    {src ? (
      <img
        className='absolute inset-0 size-full object-contain'
        src={src}
        alt={alt}
        loading='lazy'
        decoding='async'
      />
    ) : (
      <span className='text-muted-foreground text-sm'>{alt}</span>
    )}
  </div>
);

export const MainPackshotCard: FC<{
  photo: ClusterDetailResponseOutputPhotosItem;
  name: string | null;
  others: number;
}> = ({ photo, name, others }) => {
  const { t } = useTranslation();
  return (
    <aside className='border-brand bg-background sticky top-[calc(var(--app-topbar-height)+0.75rem)] flex flex-col gap-2 overflow-hidden rounded-xl border-4 pb-3 shadow-md'>
      {/* Dark text on the orange keeps the header readable. */}
      <div className='bg-brand px-3 py-2 font-bold text-neutral-950'>
        {t('clusters.review.mainPackshot')}
      </div>
      <PhotoFrame
        className='mx-2'
        src={photo.front}
        alt={t('clusters.review.mainPackshot')}
      />
      <div className='px-3 text-sm font-semibold'>{name}</div>
      <div className='text-muted-foreground px-3 text-xs'>
        {t('clusters.review.mainPackshotHint')} ({others})
      </div>
    </aside>
  );
};

interface ClusterPhotoTileProps {
  photo: ClusterDetailResponseOutputPhotosItem;
  mark: ClusterMark | undefined;
  // The photo had a saved decision before this page opened.
  decided?: boolean;
  // The reviewer changed that saved decision; saved on Done.
  changed?: boolean;
  // Someone else has the cluster open: the marks can't be changed.
  readOnly?: boolean;
  onOpen: () => void;
  onMark: (mark: ClusterMark) => void;
}

export const ClusterPhotoTile: FC<ClusterPhotoTileProps> = ({
  photo,
  mark,
  decided = false,
  changed = false,
  readOnly = false,
  onOpen,
  onMark,
}: ClusterPhotoTileProps) => {
  const { t, i18n } = useTranslation();
  const label = (kind: ClusterMark) =>
    kind === 'junk' ? t('clusters.review.junk') : t('clusters.review.wrongSku');
  // A decided photo left approved reads as approved at a glance.
  const approved = decided && !mark;

  return (
    <div
      className={cn(
        'bg-background relative flex flex-col gap-2 rounded-xl border p-2',
        approved && 'border-success bg-success/10',
        mark === 'junk' && 'border-removed bg-removed/10',
        mark === 'wrongSku' && 'border-destructive bg-destructive/10'
      )}>
      <span className='absolute top-3 right-3 z-10 flex gap-1'>
        {changed && (
          <span className='bg-brand rounded px-1.5 py-0.5 text-xs font-bold text-neutral-950'>
            {t('clusters.review.changed')}
          </span>
        )}
        {(mark ?? approved) && (
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-xs font-bold',
              mark ? MARK_STYLES[mark] : 'bg-success text-success-foreground'
            )}>
            {mark ? label(mark) : t('clusters.review.approvedTag')}
          </span>
        )}
      </span>
      <button
        data-testid='cluster-photo-open'
        type='button'
        className='cursor-pointer rounded-lg'
        aria-label={t('clusters.review.compareTitle')}
        onClick={onOpen}>
        <PhotoFrame
          className={cn(
            mark === 'junk' && '[&_img]:opacity-40 [&_img]:grayscale',
            approved &&
              'after:bg-success/15 after:pointer-events-none after:absolute after:inset-0'
          )}
          src={photo.front}
          alt={t('clusters.review.front')}
        />
      </button>
      {decided && photo.reviewedBy && photo.reviewedAt && (
        <span
          className='text-muted-foreground truncate px-0.5 text-xs'
          title={photo.reviewedBy}>
          {t('clusters.review.decidedBy', {
            who: photo.reviewedBy.split('@')[0],
            date: new Date(photo.reviewedAt).toLocaleDateString(i18n.language, {
              day: 'numeric',
              month: 'short',
            }),
          })}
        </span>
      )}
      <div className='flex flex-wrap gap-2'>
        {(['junk', 'wrongSku'] as const).map((kind) => (
          <Button
            data-testid={`cluster-photo-${kind === 'junk' ? 'junk' : 'wrong-sku'}`}
            key={kind}
            variant='outline'
            disabled={readOnly}
            aria-pressed={mark === kind}
            className={cn(
              'h-9 min-w-fit flex-1 gap-1.5 px-2.5 has-[>svg]:px-2.5',
              mark === kind && MARK_STYLES[kind]
            )}
            onClick={() => {
              onMark(kind);
            }}>
            <MarkIcon mark={kind} />
            {label(kind)}
          </Button>
        ))}
      </div>
    </div>
  );
};
